import { VACATION_PROTECTION_DAYS } from "@/lib/constants";
import { getTowerLevel } from "@/lib/tower";
import { getUpcomingVacationDays, teamIsOnVacation } from "@/lib/utils";
import { Match, MatchStatus, Team, TeamStatus, Tournament } from "@/types";
import { subDays } from "date-fns";
import { useEffect, useMemo, useState } from "react";

interface CanChallengeResult {
  canChallenge: boolean;
  reason?: string;
  cardType?: "joker" | "braveheart";
  vacationStartsInDays?: number; // Days until target team's vacation (if within tournament vacation protection window)
}

/**
 * Calculates when the challenge cooldown expires for the given team/tournament.
 * Returns null if no cooldown is active.
 */
function getCooldownEndDate(
  team: Team,
  tournament: Tournament,
): Date | null {
  const cooldownHours = tournament.challengeCooldownHours ?? 24;
  if (cooldownHours <= 0 || !team.lastMatchScheduledAt) return null;

  const scheduledMatchTime = new Date(team.lastMatchScheduledAt);
  const cooldownEndDate = new Date(scheduledMatchTime);
  cooldownEndDate.setHours(cooldownEndDate.getHours() + cooldownHours);
  return cooldownEndDate;
}

/**
 * Calculates when the defend cooldown expires for the given team/tournament.
 * Returns null if no defend cooldown is active.
 */
function getDefendCooldownEndDate(
  team: Team,
  tournament: Tournament,
): Date | null {
  const defendCooldownHours = tournament.defendCooldownHours ?? 0;
  if (defendCooldownHours <= 0 || !team.lastDefendMatchScheduledAt)
    return null;

  const scheduledMatchTime = new Date(team.lastDefendMatchScheduledAt);
  const cooldownEndDate = new Date(scheduledMatchTime);
  cooldownEndDate.setHours(
    cooldownEndDate.getHours() + defendCooldownHours,
  );
  return cooldownEndDate;
}

/**
 * Returns the later of challenge cooldown and defend cooldown end dates.
 * Returns null if neither cooldown is active.
 */
function getEffectiveCooldownEndDate(
  team: Team,
  tournament: Tournament,
): Date | null {
  const challengeEnd = getCooldownEndDate(team, tournament);
  const defendEnd = getDefendCooldownEndDate(team, tournament);

  if (!challengeEnd && !defendEnd) return null;
  if (!challengeEnd) return defendEnd;
  if (!defendEnd) return challengeEnd;

  return challengeEnd > defendEnd ? challengeEnd : defendEnd;
}

/**
 * Determines if the current team can challenge the target team
 *
 * @param currentTeam - The user's current team
 * @param targetTeam - The team to potentially challenge
 * @param tournament - Tournament configuration
 * @param sortedTeams - All teams sorted by ranking (optional, will use tournament.teams if not provided)
 * @returns Object with canChallenge boolean and optional reason string
 */
export function useCanChallenge(
  currentTeam: Team | null,
  targetTeam: Team | null,
  tournament: Tournament | null,
  sortedTeams?: Team[],
  matches?: Match[],
): CanChallengeResult {
  // Track cooldown expiry so the hook re-evaluates when cooldown ends
  const [cooldownExpired, setCooldownExpired] = useState(false);

  useEffect(() => {
    if (!currentTeam || !tournament) return;

    const endDate = getEffectiveCooldownEndDate(currentTeam, tournament);
    if (!endDate) return;

    const now = new Date();
    const remainingMs = endDate.getTime() - now.getTime();

    if (remainingMs <= 0) {
      // Already expired
      setCooldownExpired(true);
      return;
    }

    // Reset and schedule a timer to flip when cooldown expires
    setCooldownExpired(false);
    const timer = setTimeout(() => {
      setCooldownExpired(true);
    }, remainingMs);

    return () => clearTimeout(timer);
  }, [currentTeam, tournament]);

  return useMemo(() => {
    if (!currentTeam || !tournament || !targetTeam) {
      return { canChallenge: false, reason: "notInTeam" };
    }

    // Always compute vacation info upfront so it's available regardless of challenge eligibility
    const protectionDays =
      tournament.vacationProtectionDays ?? VACATION_PROTECTION_DAYS;
    const vacationDays = !teamIsOnVacation(targetTeam)
      ? getUpcomingVacationDays(targetTeam, protectionDays)
      : null;
    const vacationStartsInDays =
      vacationDays !== null ? vacationDays : undefined;

    // Create timestamp inside useMemo to ensure it's evaluated when deps change
    const now = new Date();

    // Check if tournament has started
    const tournamentStart = new Date(tournament.startsAt);
    if (now < tournamentStart) {
      return {
        canChallenge: false,
        reason: "tournamentNotStarted",
        vacationStartsInDays,
      };
    }

    // Check if we're within 7 days of tournament end (challenges locked)
    const tournamentEnd = new Date(tournament.endsAt);
    const challengeDeadline = subDays(tournamentEnd, 7);
    if (now >= challengeDeadline) {
      return {
        canChallenge: false,
        reason: "tournamentEndingSoon",
        vacationStartsInDays,
      };
    }

    // Cannot challenge yourself
    if (currentTeam.id === targetTeam.id) {
      return {
        canChallenge: false,
        reason: "cannotChallengeYourself",
        vacationStartsInDays,
      };
    }

    // Check if current team is on vacation (cannot challenge while on vacation)
    if (teamIsOnVacation(currentTeam)) {
      return {
        canChallenge: false,
        reason: "teamOnVacation",
        vacationStartsInDays,
      };
    }

    // Forfeit teams cannot interact with the tower at all.
    if (currentTeam.status === TeamStatus.FORFEIT) {
      return {
        canChallenge: false,
        reason: "teamForfeited",
        vacationStartsInDays,
      };
    }

    // Check if current team is available to challenge
    if (currentTeam.status !== TeamStatus.AVAILABLE) {
      return {
        canChallenge: false,
        reason: "teamNotAvailable",
        vacationStartsInDays,
      };
    }

    // Check if target team is on vacation (defensive check, status should already be ON_VACATION)
    if (teamIsOnVacation(targetTeam)) {
      return { canChallenge: false, reason: "targetOnVacation" };
    }

    // Check if target team has vacation starting within the protection period
    // Teams cannot be challenged in the days leading up to their vacation
    if (vacationStartsInDays !== undefined) {
      return {
        canChallenge: false,
        reason: "targetVacationSoon",
        vacationStartsInDays,
      };
    }

    // Check if target team is available or forfeit. FORFEIT teams can be
    // challenged — the challenge resolves as an instant walkover win.
    if (
      targetTeam.status !== TeamStatus.AVAILABLE &&
      targetTeam.status !== TeamStatus.FORFEIT
    ) {
      return { canChallenge: false, reason: "targetNotAvailable" };
    }

    // Check cooldowns: team cannot challenge until both challenge and defend cooldowns expire
    const effectiveCooldownEnd = getEffectiveCooldownEndDate(
      currentTeam,
      tournament,
    );
    if (effectiveCooldownEnd && now < effectiveCooldownEnd) {
      return { canChallenge: false, reason: "challengeCooldown" };
    }

    // Check preventLoserRematch: if the two teams just played each other,
    // block until either team has played a different opponent
    if (tournament.preventLoserRematch && matches) {
      const completedMatches = matches
        .filter((m) => m.status === MatchStatus.COMPLETED)
        .sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        );

      // Find the most recent match between these two teams (regardless of who was challenger/defender)
      const blockingMatch = completedMatches.find(
        (m) =>
          (m.team1Id === currentTeam.id && m.team2Id === targetTeam.id) ||
          (m.team1Id === targetTeam.id && m.team2Id === currentTeam.id),
      );

      if (blockingMatch) {
        const blockingTime = new Date(blockingMatch.updatedAt).getTime();

        // Check if any completed match after blockingMatch involves either team with a different opponent
        const blockLifted = completedMatches.some((m) => {
          if (new Date(m.updatedAt).getTime() <= blockingTime) return false;

          const involvesCurrentTeam =
            m.team1Id === currentTeam.id || m.team2Id === currentTeam.id;
          const involvesTargetTeam =
            m.team1Id === targetTeam.id || m.team2Id === targetTeam.id;

          if (!involvesCurrentTeam && !involvesTargetTeam) return false;

          // Check that the match involves a different opponent
          const involvesBothTeams = involvesCurrentTeam && involvesTargetTeam;
          return !involvesBothTeams;
        });

        if (!blockLifted) {
          return { canChallenge: false, reason: "loserCannotRematch" };
        }
      }
    }

    // Get all active teams and sort by ranking
    // IMPORTANT: Include teams with ALL statuses (available, challenged, injured, on vacation, etc.)
    // because they all occupy positions in the tower for level calculation
    const teams = sortedTeams || tournament.teams || [];
    const allTeams = teams
      .filter((t) => t.isActive && t.isPlacedInTower !== false)
      .sort((a, b) => a.ranking - b.ranking);

    // Find positions in the sorted array (1-based)
    const currentPosition =
      allTeams.findIndex((t) => t.id === currentTeam.id) + 1;
    const targetPosition =
      allTeams.findIndex((t) => t.id === targetTeam.id) + 1;

    if (currentPosition === 0 || targetPosition === 0) {
      return { canChallenge: false, reason: "teamNotFound" };
    }

    // Calculate levels based on array position, not ranking field
    const currentLevel = getTowerLevel(
      currentPosition,
      tournament.towerTeamsPerRow,
      tournament.towerTopRowTeamCount,
    );
    const targetLevel = getTowerLevel(
      targetPosition,
      tournament.towerTeamsPerRow,
      tournament.towerTopRowTeamCount,
    );

    // Can only challenge teams above (lower level number = higher position)
    if (targetLevel >= currentLevel) {
      return { canChallenge: false, reason: "cannotChallengeLowerRank" };
    }

    const levelDifference = currentLevel - targetLevel;

    // Can always challenge 1 level above without any card
    if (levelDifference === 1) {
      return { canChallenge: true };
    }

    // 2 levels above: requires joker OR braveheart card
    if (levelDifference === 2) {
      // Prefer joker card over braveheart
      if (currentTeam.jokerCards > 0) {
        return { canChallenge: true, cardType: "joker" };
      }
      // If no joker, can use braveheart
      if (currentTeam.braveheartCards > 0) {
        return { canChallenge: true, cardType: "braveheart" };
      }
      return { canChallenge: false, reason: "needsJokerOrBraveheartCard" };
    }

    // 3+ levels above: requires braveheart card (only card that allows any level)
    if (levelDifference > 2) {
      if (currentTeam.braveheartCards > 0) {
        return { canChallenge: true, cardType: "braveheart" };
      }
      return { canChallenge: false, reason: "needsBraveheartCard" };
    }

    return { canChallenge: false, reason: "unknown" };
  }, [currentTeam, targetTeam, tournament, sortedTeams, matches, cooldownExpired]);
}
